// Knowledge Base: FAQ / article CRUD, Draft -> Published -> Archived transitions and simulated
// document/URL import jobs. (The simulated bot reply does not read these — RAG belongs to the
// real backend.)
import { knowledgeStore, importsStore } from "../core";
import { ok, fail, validationFail, genId, nowIso, relativeTime } from "../helpers";
import { collect, firstMessage, lengthError, oneOfError, urlError } from "../validate";

const IMPORT_DURATION_MS = 6000; // wall-clock-driven, so progress is real elapsed time
const STATUSES = ["Draft", "Published", "Archived"];
const CATEGORIES = ["Product", "Pricing", "Support", "Getting Started", "Company"];

function itemFields(b, partial) {
  const has = (k) => !partial || b[k] !== undefined;
  return collect([
    has("title") && ["title", lengthError(b.title, { min: 2, max: 120, label: "Title", required: true })],
    b.category !== undefined && ["category", oneOfError(b.category, CATEGORIES, "Category")],
    b.question !== undefined && ["question", lengthError(b.question, { max: 300, label: "Question" })],
    b.answer !== undefined && ["answer", lengthError(b.answer, { max: 5000, label: "Answer" })],
    b.url !== undefined && b.url !== "" && ["url", urlError(b.url, { label: "URL" })]
  ].filter(Boolean));
}

const itemView = (item) => ({ ...item, updated: relativeTime(item.updatedAt) });

function jobView(job) {
  const elapsed = Date.now() - new Date(job.startedAt).getTime();
  const progress = Math.min(100, Math.round((elapsed / job.durationMs) * 100));
  return { ...job, progress, status: progress >= 100 ? "Completed" : "Processing" };
}

// Completed jobs materialise exactly one Draft item (idempotent via job.completed).
function completeJobIfDone(job) {
  const view = jobView(job);
  if (view.status !== "Completed" || job.completed) return view;
  job.completed = true;
  const items = knowledgeStore.all();
  const item = {
    id: genId("kb"), tenantId: job.tenantId, title: job.title, type: "Article", category: job.category || "Support",
    source: job.kind === "url" ? "URL" : "Document", status: "Draft", updatedAt: nowIso(), views: 0, question: "", answer: "",
    url: job.url || "", fileName: job.fileName || ""
  };
  knowledgeStore.save([...items, item]);
  job.itemId = item.id;
  return { ...view, itemId: item.id };
}

const publishBlocker = (item) => {
  const hasContent = item.type === "FAQ" ? !!(item.answer || "").trim() : (!!(item.answer || "").trim() || !!item.url || !!item.fileName);
  return hasContent ? null : "Add an answer or content before publishing.";
};

export const knowledgeRoutes = [
  {
    method: "POST", pattern: /^\/api\/knowledge\/import$/, permission: "knowledge:manage",
    handler: ({ tenantId, body }) => {
      const kind = body.kind === "url" ? "url" : "document";
      const fileOk = /\.(pdf|docx?|txt)$/i.test(String(body.fileName || "").trim());
      const fields = collect([
        kind === "url" ? ["url", urlError(body.url, { required: true, label: "URL" })] : null,
        kind === "document" ? ["fileName", !String(body.fileName || "").trim() ? "Choose a document to import." : (fileOk ? "" : "Only .pdf, .doc, .docx or .txt documents can be imported.")] : null,
        body.title !== undefined && body.title !== "" ? ["title", lengthError(body.title, { min: 2, max: 120, label: "Title" })] : null,
        body.category !== undefined ? ["category", oneOfError(body.category, CATEGORIES, "Category")] : null
      ].filter(Boolean));
      if (fields) return validationFail(fields, firstMessage(fields));
      const title = (body.title || "").trim() || (kind === "url" ? body.url : body.fileName);
      const jobs = importsStore.all();
      const job = {
        id: genId("imp"), tenantId, kind, title, fileName: body.fileName || "", url: body.url || "",
        category: body.category || "Support", startedAt: nowIso(), durationMs: IMPORT_DURATION_MS
      };
      importsStore.save([job, ...jobs].slice(0, 30));
      return ok(201, jobView(job));
    }
  },
  {
    method: "GET", pattern: /^\/api\/knowledge\/import$/, permission: "knowledge:manage",
    handler: ({ tenantId, scenario }) => {
      const jobs = importsStore.all();
      const items = scenario === "empty" ? [] : jobs.filter((j) => j.tenantId === tenantId).map(completeJobIfDone);
      importsStore.save(jobs);
      return ok(200, items, { total: items.length });
    }
  },
  {
    method: "GET", pattern: /^\/api\/knowledge\/import\/([\w-]+)$/, permission: "knowledge:manage",
    handler: ({ tenantId, match }) => {
      const jobs = importsStore.all();
      const job = jobs.find((j) => j.id === match[1] && j.tenantId === tenantId);
      if (!job) return fail(404, "NOT_FOUND", "Import job not found.");
      const view = completeJobIfDone(job);
      importsStore.save(jobs);
      return ok(200, view);
    }
  },
  {
    method: "GET", pattern: /^\/api\/knowledge$/, permission: "knowledge:manage",
    handler: ({ tenantId, query, scenario }) => {
      let items = scenario === "empty" ? [] : knowledgeStore.all().filter((k) => k.tenantId === tenantId);
      const status = query.get("status");
      if (status) items = items.filter((k) => k.status === status);
      return ok(200, items.map(itemView), { total: items.length, tenantId });
    }
  },
  {
    method: "GET", pattern: /^\/api\/knowledge\/([\w-]+)$/, permission: "knowledge:manage",
    handler: ({ tenantId, match }) => {
      const item = knowledgeStore.all().find((k) => k.id === match[1] && k.tenantId === tenantId);
      if (!item) return fail(404, "NOT_FOUND", "Knowledge item not found.");
      return ok(200, itemView(item));
    }
  },
  {
    method: "POST", pattern: /^\/api\/knowledge$/, permission: "knowledge:manage",
    handler: ({ tenantId, body }) => {
      const invalid = itemFields(body, false);
      if (invalid) return validationFail(invalid, firstMessage(invalid));
      const items = knowledgeStore.all();
      const item = {
        id: genId("kb"), tenantId, title: String(body.title).trim(), type: body.type === "FAQ" ? "FAQ" : "Article",
        category: body.category || "Product", source: body.source || "Manual", status: "Draft", updatedAt: nowIso(), views: 0,
        question: body.question || "", answer: body.answer || "", url: body.url || "", fileName: body.fileName || ""
      };
      knowledgeStore.save([...items, item]);
      return ok(201, itemView(item));
    }
  },
  {
    method: "PATCH", pattern: /^\/api\/knowledge\/([\w-]+)$/, permission: "knowledge:manage",
    handler: ({ tenantId, match, body }) => {
      const items = knowledgeStore.all();
      const item = items.find((k) => k.id === match[1] && k.tenantId === tenantId);
      if (!item) return fail(404, "NOT_FOUND", "Knowledge item not found.");
      const invalid = itemFields(body, true);
      if (invalid) return validationFail(invalid, firstMessage(invalid));
      if (body.status !== undefined) {
        if (!STATUSES.includes(body.status)) return validationFail({ status: "Status must be Draft, Published or Archived." });
        const merged = { ...item, ...body };
        if (body.status === "Published") {
          const blocker = publishBlocker(merged);
          if (blocker) return validationFail({ answer: blocker }, blocker);
        }
      }
      ["title", "category", "question", "answer", "status", "type", "url"].forEach((key) => { if (body[key] !== undefined) item[key] = body[key]; });
      item.updatedAt = nowIso();
      knowledgeStore.save(items);
      return ok(200, itemView(item));
    }
  },
  {
    method: "DELETE", pattern: /^\/api\/knowledge\/([\w-]+)$/, permission: "knowledge:manage",
    handler: ({ tenantId, match }) => {
      const items = knowledgeStore.all();
      if (!items.some((k) => k.id === match[1] && k.tenantId === tenantId)) return fail(404, "NOT_FOUND", "Knowledge item not found.");
      knowledgeStore.save(items.filter((k) => k.id !== match[1]));
      return ok(200, { id: match[1] });
    }
  }
];
